import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { API_ENDPOINTS } from '../utils/config';

export interface User {
    _id: string;
    email: string;
    name: string;
    role: string;
    company_id?: string;
    company?: any;
    permissions?: any;
    is_admin?: boolean;
    account_type?: 'b2c' | 'b2b';
}

interface AuthContextType {
    user: User | null;
    token: string | null;
    login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
    loginWithToken: (token: string) => Promise<{ success: boolean; user?: User; error?: string }>;
    logout: () => void;
    isLoading: boolean;
    isAuthenticated: boolean;
}

interface AuthProviderProps {
    children: ReactNode;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [token, setToken] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    // Check for saved token on startup
    useEffect(() => {
        // Hard short-circuit: if the user landed on /logout (e.g. redirected
        // from a sub-app), wipe local state immediately and skip the token
        // validation roundtrip. Without this we would briefly re-authenticate
        // the user before LogoutPage's effect runs, racing with redirects.
        if (typeof window !== 'undefined' && window.location.pathname === '/logout') {
            try {
                localStorage.removeItem('auth_token');
                localStorage.removeItem('auth_user');
            } catch { /* ignore */ }
            setIsLoading(false);
            return;
        }

        const savedToken = localStorage.getItem('auth_token');
        const savedUser = localStorage.getItem('auth_user');

        if (savedToken && savedUser) {
            // Use config endpoint
            const meUrl = `${API_ENDPOINTS.USERS}/me`;
            console.log('🔍 DEBUG: Validating token via:', meUrl);

            fetch(meUrl, {
                headers: {
                    'Authorization': `Bearer ${savedToken}`,
                    'Content-Type': 'application/json',
                },
            })
                .then(response => {
                    if (response.ok) {
                        setToken(savedToken);
                        setUser(JSON.parse(savedUser));
                    } else {
                        localStorage.removeItem('auth_token');
                        localStorage.removeItem('auth_user');
                    }
                    setIsLoading(false);
                })
                .catch(() => {
                    localStorage.removeItem('auth_token');
                    localStorage.removeItem('auth_user');
                    setIsLoading(false);
                });
        } else {
            setIsLoading(false);
        }
    }, []);

    const login = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
        try {
            console.log('🔍 DEBUG: Attempting login to:', API_ENDPOINTS.LOGIN);
            const response = await fetch(API_ENDPOINTS.LOGIN, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password }),
            });

            let data;
            const responseText = await response.text();
            try {
                data = JSON.parse(responseText);
            } catch (e) {
                console.error('❌ JSON Parse Error:', e);
                console.error('📄 Raw Response:', responseText);
                return { success: false, error: 'Server returned invalid response (HTML instead of JSON)' };
            }

            if (data.success && data.data?.access_token) {
                const accessToken = data.data.access_token;

                // Get user info
                // Use explicit config value - if this is undefined, it means config.ts is stale
                const meUrl = `${API_ENDPOINTS.USERS}/me`;
                console.log('🔍 DEBUG: Fetching user info from:', meUrl);

                // Keep this check to fail gracefully with a clear error if config is broken
                if (!API_ENDPOINTS.USERS) {
                    console.error('❌ CRITICAL CONFIG ERROR: API_ENDPOINTS.USERS is undefined!');
                    console.log('Full Enpoints Object:', API_ENDPOINTS);
                }

                const meResponse = await fetch(meUrl, {
                    headers: { 'Authorization': `Bearer ${accessToken}` }
                });

                let meData;
                const meResponseText = await meResponse.text();
                try {
                    meData = JSON.parse(meResponseText);
                } catch (e) {
                    console.error('❌ /me JSON Parse Error:', e);
                    console.error('📄 Raw /me Response:', meResponseText);
                    return { success: false, error: 'Failed to retrieve user data (invalid server response)' };
                }

                if (meData.success && meData.data) {
                    setToken(accessToken);
                    setUser(meData.data);
                    localStorage.setItem('auth_token', accessToken);
                    localStorage.setItem('auth_user', JSON.stringify(meData.data));
                    return { success: true };
                }
            }

            return { success: false, error: data.error || data.message || 'Login failed' };
        } catch (error: any) {
            return { success: false, error: error.message || 'Login failed' };
        }
    };

    const loginWithToken = async (accessToken: string): Promise<{ success: boolean; user?: User; error?: string }> => {
        try {
            const meUrl = `${API_ENDPOINTS.USERS}/me`;
            const meResponse = await fetch(meUrl, {
                headers: { 'Authorization': `Bearer ${accessToken}` }
            });
            const meData = await meResponse.json();

            if (meData.success && meData.data) {
                setToken(accessToken);
                setUser(meData.data);
                localStorage.setItem('auth_token', accessToken);
                localStorage.setItem('auth_user', JSON.stringify(meData.data));
                return { success: true, user: meData.data };
            }

            return { success: false, error: 'Failed to get user data' };
        } catch (error: any) {
            return { success: false, error: error.message };
        }
    };

    const logout = () => {
        setUser(null);
        setToken(null);
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
    };

    const value: AuthContextType = {
        user,
        token,
        login,
        loginWithToken,
        logout,
        isLoading,
        isAuthenticated: !!user && !!token,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
